// Force-directed graph overlay (Esc): the whole graph as a physical thing —
// and a live decision surface. Open questions carry their options as small
// clickable satellites: click one to decide right here; the graph rebuilds
// in place as the new frontier lands. Click a committed node to jump there.

import { bus } from "../core/bus";
import { jumpTo, matchOptionLabel, pick, tokenOverlap } from "../core/navigation";
import { state, topBranchOf, type DecisionNode } from "../core/state";
import { clamp, truncate } from "../lib/text";
import { $ } from "./dom";
import { toast } from "./toast";

interface FgNode {
  id: string;
  ref: Pick<DecisionNode, "depth" | "chosenIndex" | "id" | "verdict"> & Partial<DecisionNode>;
  x: number; y: number; vx: number; vy: number;
  ghost?: boolean;
  label?: string;
  /** an undecided option of an open question — clickable to decide */
  option?: { parentId: string; index: number; recommend: boolean; ready: boolean };
}
interface FgEdge {
  a: number;
  b: number;
  ghost?: boolean;        // reviewer-proposed alternative line (dashed, springy)
  constraint?: boolean;   // cross-branch dependency (dashed, exerts NO force)
  option?: boolean;       // question → candidate option (thin, short spring)
}

const FG = {
  open: false,
  nodes: [] as FgNode[],
  edges: [] as FgEdge[],
  pan: { x: 0, y: 0 },
  scale: 1,
  raf: 0,
  drag: null as null | { node: FgNode | null; sx: number; sy: number },
  moved: 0,
  pendingOpt: null as string | null,   // satellite id being generated after a click
  pendingSince: 0,
};

export function isGraphOpen(): boolean { return FG.open; }

function fgBuild(): void {
  const ns = [...state.nodes.values()];
  const idx = new Map(ns.map((n, i) => [n.id, i]));
  const old = new Map(FG.nodes.map((n) => [n.id, n]));
  FG.nodes = ns.map((n, i) => {
    const o = old.get(n.id);
    const ang = i * 2.39996;               // golden-angle spiral seeding
    return { id: n.id, ref: n,
      x: o ? o.x : Math.cos(ang) * (60 + 26 * i), y: o ? o.y : Math.sin(ang) * (60 + 26 * i),
      vx: 0, vy: 0 };
  });
  FG.edges = ns.filter((n) => n.parentId && idx.has(n.parentId))
    .map((n) => ({ a: idx.get(n.parentId!)!, b: idx.get(n.id)! }));

  // reviewer-proposed alternatives hang off their source node as dashed ghosts
  let gi = 0;
  for (const l of state.reviews.filter((x) => x.enabled)) {
    for (const e of l.events) {
      if (e.t !== "branch" || !idx.has(e.fromNodeId)) continue;
      let prev = idx.get(e.fromNodeId)!;
      (e.nodes || []).forEach((s, k) => {
        const src = FG.nodes[prev]!;
        FG.nodes.push({
          id: `ghost-${gi}-${k}`, ghost: true,
          label: truncate(k === 0 ? e.label : s.take || s.question, 26),
          x: src.x + 40 + k * 10, y: src.y + 40, vx: 0, vy: 0,
          ref: { depth: (src.ref?.depth ?? 1) + 1 + k, chosenIndex: null, id: "", verdict: undefined },
        });
        FG.edges.push({ a: prev, b: FG.nodes.length - 1, ghost: true });
        prev = FG.nodes.length - 1;
      });
      gi++;
    }
  }

  // open questions: every undecided option becomes a small clickable
  // satellite — the decisions yet to be made, visible and actionable here
  for (const n of ns) {
    if (n.chosenIndex != null || !n.options.length) continue;
    const parentIdx = idx.get(n.id)!;
    const src = FG.nodes[parentIdx]!;
    n.options.forEach((o, oi) => {
      const sid = `opt-${n.id}-${oi}`;
      const prev = old.get(sid);
      const ang = (oi / n.options.length) * Math.PI * 2;
      FG.nodes.push({
        id: sid,
        option: {
          parentId: n.id, index: oi,
          recommend: (n.recommend ?? 0) === oi,
          ready: n.prefetch[oi]?.status === "ready" || !!n.childId[oi],
        },
        label: truncate(o.label, 24),
        x: prev ? prev.x : src.x + Math.cos(ang) * 60,
        y: prev ? prev.y : src.y + Math.sin(ang) * 60,
        vx: 0, vy: 0,
        ref: { depth: n.depth + 1, chosenIndex: null, id: "", verdict: undefined },
      });
      FG.edges.push({ a: parentIdx, b: FG.nodes.length - 1, option: true });
    });
  }
  if (FG.pendingOpt && (!FG.nodes.some((n) => n.id === FG.pendingOpt) || performance.now() - FG.pendingSince > 15000)) {
    FG.pendingOpt = null;   // the question resolved (satellite gone) or the pick gave up
  }

  // cross-branch dependencies: each pinned constraint links the branches it
  // names, dashed and non-forcing — pure annotation, the physics ignores it
  const root = state.rootId ? state.nodes.get(state.rootId) : null;
  if (root && state.constraints.length) {
    const anchorFor = (branchLabel: string): number | null => {
      const oi = matchOptionLabel(root, branchLabel);
      if (oi != null) {
        // link the deepest resolved node in the branch (the live frontier of
        // that line of decisions); fall back to the branch's entry node
        const label = root.options[oi]?.label ?? "";
        const childId = root.childId[oi];
        let deepest: DecisionNode | null = childId ? state.nodes.get(childId) ?? null : null;
        for (const n of state.nodes.values()) {
          if (n.depth > 0 && n.chosenIndex != null && topBranchOf(n) === label) {
            if (!deepest || n.depth > deepest.depth) deepest = n;
          }
        }
        if (deepest) return idx.get(deepest.id) ?? null;
      }
      // the compiler's branch strings drift from the root menu — fall back to
      // matching each node's own free-text branch field
      let best: DecisionNode | null = null, bestScore = 0.5;
      for (const n of state.nodes.values()) {
        if (n.depth === 0 || n.chosenIndex == null || !n.branch) continue;
        const score = tokenOverlap(branchLabel, n.branch);
        if (score > bestScore || (score === bestScore && best && n.depth > best.depth)) { best = n; bestScore = score; }
      }
      return best ? idx.get(best.id) ?? null : null;
    };
    for (const c of state.constraints) {
      const anchors = [...new Set(c.branches.map(anchorFor).filter((v): v is number => v != null))];
      for (let i = 0; i < anchors.length; i++)
        for (let j = i + 1; j < anchors.length; j++)
          FG.edges.push({ a: anchors[i]!, b: anchors[j]!, constraint: true });
    }
  }
}

function fgTick(): void {
  const N = FG.nodes;
  for (let i = 0; i < N.length; i++) {
    for (let j = i + 1; j < N.length; j++) {
      const a = N[i]!, b = N[j]!;
      let dx = b.x - a.x, dy = b.y - a.y;
      const d2 = dx * dx + dy * dy || 1, d = Math.sqrt(d2);
      const f = Math.min(3200 / d2, 4);
      dx /= d; dy /= d;
      a.vx -= dx * f; a.vy -= dy * f;
      b.vx += dx * f; b.vy += dy * f;
    }
  }
  for (const e of FG.edges) {
    if (e.constraint) continue;   // annotation only — never pulls the layout
    const a = N[e.a]!, b = N[e.b]!;
    let dx = b.x - a.x, dy = b.y - a.y;
    const d = Math.sqrt(dx * dx + dy * dy) || 1;
    const f = (d - (e.option ? 52 : 95)) * 0.03;   // options orbit close
    dx /= d; dy /= d;
    a.vx += dx * f; a.vy += dy * f;
    b.vx -= dx * f; b.vy -= dy * f;
  }
  for (const n of N) {
    n.vx -= n.x * 0.004; n.vy -= n.y * 0.004;       // gentle centering
    if (FG.drag?.node === n) { n.vx = 0; n.vy = 0; continue; }
    n.vx *= 0.82; n.vy *= 0.82;
    n.x += n.vx; n.y += n.vy;
  }
}

function fgDraw(): void {
  const cv = $("#graphCanvas") as HTMLCanvasElement;
  const ctx = cv.getContext("2d")!;
  const dpr = window.devicePixelRatio || 1;
  const W = cv.clientWidth, H = cv.clientHeight;
  if (cv.width !== W * dpr) { cv.width = W * dpr; cv.height = H * dpr; }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);
  const cs = getComputedStyle(document.documentElement);
  const C = { line: "rgba(120,130,160,0.35)", dim: cs.getPropertyValue("--dim").trim() || "#8b93a7",
    text: cs.getPropertyValue("--text").trim() || "#e7e9f0", accent: cs.getPropertyValue("--accent").trim() || "#6ea8fe",
    good: "#4ade80", warn: "#fbbf24", hot: "#f87171", panel: cs.getPropertyValue("--panel-2").trim() || "#1a1d29" };
  const sx = (x: number) => W / 2 + (x + FG.pan.x) * FG.scale;
  const sy = (y: number) => H / 2 + (y + FG.pan.y) * FG.scale;
  ctx.lineWidth = 1;
  ctx.strokeStyle = C.line;
  for (const e of FG.edges) {
    const a = FG.nodes[e.a]!, b = FG.nodes[e.b]!;
    ctx.setLineDash(e.constraint ? [2, 6] : e.ghost ? [4, 4] : e.option ? [2, 3] : []);
    ctx.strokeStyle = e.constraint ? C.accent : e.ghost ? C.warn : C.line;
    ctx.globalAlpha = e.constraint ? 0.7 : e.option ? 0.55 : 1;
    ctx.beginPath(); ctx.moveTo(sx(a.x), sy(a.y)); ctx.lineTo(sx(b.x), sy(b.y)); ctx.stroke();
    if (e.constraint) {   // ⛓ at the midpoint marks it as a pinned tension
      ctx.fillStyle = C.accent;
      ctx.font = "10px ui-sans-serif, system-ui";
      ctx.textAlign = "center";
      ctx.fillText("⛓", (sx(a.x) + sx(b.x)) / 2, (sy(a.y) + sy(b.y)) / 2 - 3);
    }
    ctx.globalAlpha = 1;
  }
  ctx.setLineDash([]);
  const pulse = 0.55 + 0.45 * Math.sin(performance.now() / 180);
  for (const n of FG.nodes) {
    const d = n.ref, x = sx(n.x), y = sy(n.y);

    if (n.option) {
      // a decision yet to be made — small, clickable, ready-state ringed
      const r = 5.5 * FG.scale;
      const pending = FG.pendingOpt === n.id;
      ctx.setLineDash([]);
      ctx.beginPath(); ctx.arc(x, y, r, 0, 7);
      ctx.fillStyle = pending ? C.accent : C.panel;
      ctx.globalAlpha = pending ? pulse : 1;
      ctx.fill();
      ctx.strokeStyle = n.option.ready ? C.good : n.option.recommend ? C.accent : C.line;
      ctx.lineWidth = n.option.recommend ? 2 : 1.2;
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.fillStyle = n.option.recommend ? C.text : C.dim;
      ctx.font = "10px ui-sans-serif, system-ui";
      ctx.textAlign = "center";
      ctx.fillText((n.option.recommend ? "↹ " : "") + n.label!, x, y + r + 11);
      continue;
    }

    const r = (d.depth === 0 ? 13 : Math.max(6, 11 - d.depth)) * FG.scale;
    ctx.setLineDash(n.ghost ? [3, 3] : []);
    ctx.beginPath(); ctx.arc(x, y, r, 0, 7);
    const isOpenQ = !n.ghost && d.chosenIndex == null && d.depth > 0;
    ctx.fillStyle = n.ghost ? "transparent"
      : d.id === state.currentId ? C.accent : d.chosenIndex != null ? C.panel : "rgba(110,120,150,0.25)";
    ctx.fill();
    const ring = n.ghost ? C.warn
      : d.verdict ? (d.verdict.verdict === "sound" ? C.good : d.verdict.verdict === "weak" ? C.warn : C.hot)
      : d.id === state.currentId ? C.accent : isOpenQ ? C.warn : C.line;
    ctx.strokeStyle = ring; ctx.lineWidth = d.id === state.currentId ? 2.5 : isOpenQ ? 2 : 1.5;
    if (isOpenQ) ctx.setLineDash([3, 2]);   // open question: dashed warm ring
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = n.ghost ? C.warn : d.id === state.currentId ? C.text : C.dim;
    ctx.font = `${d.depth === 0 ? 12 : 11}px ui-sans-serif, system-ui`;
    ctx.textAlign = "center";
    const label = n.ghost ? n.label!
      : d.depth === 0 ? "idea" : d.depth === 1 ? topBranchOf(d as DecisionNode) || d.branch || "" : truncate(d.question || "", 30);
    ctx.fillText(label, x, y + r + 13);
  }
  ctx.setLineDash([]);
}

function fgLoop(): void {
  if (!FG.open) return;
  fgTick(); fgTick();          // settle faster
  fgDraw();
  FG.raf = requestAnimationFrame(fgLoop);
}

function fgHit(mx: number, my: number): FgNode | null {
  const cv = $("#graphCanvas") as HTMLCanvasElement;
  const W = cv.clientWidth, H = cv.clientHeight;
  // options first — they're smaller and often sit near their question
  for (const pass of [true, false]) {
    for (const n of FG.nodes) {
      if (n.ghost) continue;   // proposals aren't navigable — open the source node instead
      if (!!n.option !== pass) continue;
      const x = W / 2 + (n.x + FG.pan.x) * FG.scale, y = H / 2 + (n.y + FG.pan.y) * FG.scale;
      const hitR = (n.option ? 12 : 16) * FG.scale;
      if ((mx - x) ** 2 + (my - y) ** 2 < hitR ** 2) return n;
    }
  }
  return null;
}

/** Decide an open question from inside the graph — the overlay stays up. */
function pickFromGraph(sat: FgNode): void {
  if (FG.pendingOpt) return;               // one decision at a time
  const { parentId, index } = sat.option!;
  const parent = state.nodes.get(parentId);
  if (!parent || parent.chosenIndex != null) return;
  FG.pendingOpt = sat.id;
  FG.pendingSince = performance.now();
  if (state.currentId !== parentId) jumpTo(parentId);
  void pick(index).catch(() => { toast("couldn't decide that here — try again"); })
    .finally(() => { FG.pendingOpt = null; if (FG.open) fgBuild(); });
}

export function openGraph(): void {
  if (!state.rootId) { toast("nothing to map yet"); return; }
  fgBuild();
  FG.open = true;
  $("#graphOverlay").classList.add("show");
  fgLoop();
}

export function closeGraph(): void {
  FG.open = false;
  cancelAnimationFrame(FG.raf);
  $("#graphOverlay").classList.remove("show");
}

export function initGraph(): void {
  const cv = $("#graphCanvas") as HTMLCanvasElement;
  cv.addEventListener("mousedown", (e) => {
    const r = cv.getBoundingClientRect();
    const node = fgHit(e.clientX - r.left, e.clientY - r.top);
    FG.drag = { node, sx: e.clientX, sy: e.clientY };
    FG.moved = 0;
  });
  cv.addEventListener("mousemove", (e) => {
    if (!FG.drag) return;
    const dx = e.movementX, dy = e.movementY;
    FG.moved += Math.abs(dx) + Math.abs(dy);
    if (FG.drag.node) { FG.drag.node.x += dx / FG.scale; FG.drag.node.y += dy / FG.scale; }
    else { FG.pan.x += dx / FG.scale; FG.pan.y += dy / FG.scale; }
  });
  cv.addEventListener("mouseup", () => {
    const drag = FG.drag;
    FG.drag = null;
    if (!drag || FG.moved > 6 || !drag.node) return;   // it was a drag, not a click
    if (drag.node.option) { pickFromGraph(drag.node); return; }   // decide here, stay in the graph
    jumpTo(drag.node.id);
    closeGraph();
  });
  cv.addEventListener("wheel", (e) => {
    e.preventDefault();
    FG.scale = clamp(FG.scale * (e.deltaY < 0 ? 1.12 : 0.89), 0.3, 3);
  }, { passive: false });

  // the graph is a live view: rebuild in place (positions kept) as decisions
  // land, prefetch readiness changes, or the model refreshes a node
  const refresh = () => { if (FG.open) fgBuild(); };
  bus.on("render:all", refresh);
  bus.on("render:node", refresh);
  bus.on("prefetch:ready", refresh);

  // debug/automation hook: current screen-space node positions
  (window as any).__fgNodes = () => {
    const W = cv.clientWidth, H = cv.clientHeight;
    return FG.nodes.map((n) => ({
      id: n.id, label: n.label ?? n.ref.question ?? "", option: !!n.option,
      x: W / 2 + (n.x + FG.pan.x) * FG.scale, y: H / 2 + (n.y + FG.pan.y) * FG.scale,
    }));
  };
}
